import { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Calendar as CalendarIcon,
  FileDown,
  Search,
  ClipboardCheck,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  get_audit_report,
  get_report_filter_data,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import XLSX from "sheetjs-style";
import { handleError } from "../CommonJquery/CommonJquery";

// Backend sends 'EXCESS' | 'SHORT' | 'EQUAL' (based on physical_qty - system_qty)
const varianceTypeBadgeClass = (type) => {
  switch ((type || "").toUpperCase()) {
    case "EXCESS":
      return "bg-success/10 text-success";
    case "SHORT":
      return "bg-destructive/10 text-destructive";
    default:
      return "bg-muted text-muted-foreground";
  }
};

export default function AuditReport() {
  const { toast } = useToast();

  // Filter states
  const [filterOption, setFilterOption] = useState("");
  const [startCal, setStartCal] = useState(new Date());
  const [endCal, setEndCal] = useState(new Date());
  const [departmentId, setDepartmentId] = useState("all");
  const [varianceView, setVarianceView] = useState("all"); // all | variance

  // Filter dropdown data
  const [departments, setDepartments] = useState([]);

  // Data states
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [auditData, setAuditData] = useState([]);
  const [expandedAuditId, setExpandedAuditId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  // Stats (from API summary) - these are QUANTITIES, not currency values
  const [stats, setStats] = useState({
    totalAudits: 0,
    totalVariances: 0,
    totalGain: 0,
    totalLoss: 0,
    netVariance: 0,
  });

  // Map department_id -> department_name, since the API only returns department_id
  const departmentMap = useMemo(() => {
    const map = {};
    departments.forEach((d) => {
      map[String(d.primary_id)] = d.department_name;
    });
    return map;
  }, [departments]);

  // Format number (quantities, 2-3 decimal places)
  const formatNumber = (num) => {
    if (num === "-") return "-";
    if (num === undefined || num === null) return 0;
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return 0;
    return parsed % 1 === 0 ? parsed : parseFloat(parsed.toFixed(3));
  };

  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Fetch dropdown filter data (departments)
  const fetchFilterData = useCallback(async () => {
    try {
      const fd = new FormData();
      const response = await server_post_data(get_report_filter_data, fd);
      if (!response.data.error) {
        setDepartments(response.data.message.departments || []);
      }
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  }, []);

  // Fetch audit data
  const fetchAuditData = useCallback(
    async (start_date, end_date, flag = "1") => {
      if (flag === "2") {
        if (filterOption === "today") {
          const today = new Date();
          start_date = new Date(today.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_week") {
          const today = new Date();
          const firstDayOfWeek = new Date(
            today.setDate(today.getDate() - today.getDay()),
          );
          start_date = new Date(firstDayOfWeek.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_month") {
          const today = new Date();
          start_date = new Date(
            today.getFullYear(),
            today.getMonth(),
            1,
            0,
            0,
            0,
          );
          end_date = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0,
            23,
            59,
            59,
          );
        } else if (filterOption === "this_year") {
          const today = new Date();
          start_date = new Date(today.getFullYear(), 0, 1, 0, 0, 0);
          end_date = new Date(today.getFullYear(), 11, 31, 23, 59, 59);
        } else if (filterOption === "custom") {
          if (!startCal || !endCal) {
            toast({
              title: "Validation Error",
              description: "Please select both start and end dates.",
              variant: "destructive",
            });
            return;
          }
          start_date = startCal;
          end_date = endCal;
        }

        if (start_date > end_date) {
          toast({
            title: "Validation Error",
            description: "Start date cannot be greater than end date.",
            variant: "destructive",
          });
          return;
        }
      }

      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("start_date", formatDateLocal(start_date));
      fd.append("end_date", formatDateLocal(end_date));
      if (departmentId !== "all") fd.append("department_id", departmentId);
      fd.append(
        "variance_only",
        varianceView === "variance" ? "true" : "false",
      );

      try {
        const response = await server_post_data(get_audit_report, fd);

        if (response.data.error) {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        } else {
          const summary = response.data.summary || {};

          // Qty ab ingredient ke apne unit me; gain/loss ab ₹ me —
          // alag unit ki qty (KG + ML) jodna galat tha
          const data = (response.data.data || []).map((a) => ({
            ...a,
            audit_number: a.audit_no ?? a.audit_number,
            audit_notes: a.notes ?? a.audit_notes,
            items_with_variance:
              a.items_with_difference ?? a.items_with_variance ?? 0,
            total_gain_qty: a.gain_value ?? a.total_gain_qty ?? 0,
            total_loss_qty: a.loss_value ?? a.total_loss_qty ?? 0,
            net_variance: a.net_value ?? a.net_variance ?? 0,
            items: (a.items || []).map((i) => ({
              ...i,
              unit_name: i.unit || i.unit_name,
              variance_type:
                (i.variance_type || "").toUpperCase() === "MATCH"
                  ? "EQUAL"
                  : (i.variance_type || "").toUpperCase(),
            })),
          }));

          setAuditData(data);
          setExpandedAuditId(null);
          setStats({
            totalAudits: summary.total_audits || 0,
            totalVariances:
              summary.total_variances ??
              data.reduce((s, a) => s + (a.items_with_variance || 0), 0),
            totalGain: summary.total_gain_value ?? summary.total_gain ?? 0,
            totalLoss: summary.total_loss_value ?? summary.total_loss ?? 0,
            netVariance: summary.net_value ?? summary.net_variance ?? 0,
          });
        }
      } catch (error) {
        console.error("Error fetching audit data:", error);
        toast({
          title: "Error",
          description: "Failed to load audit data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [filterOption, startCal, endCal, departmentId, varianceView, toast],
  );

  // Handle search
  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    fetchAuditData(startCal, endCal, "2");
  }, [startCal, endCal, fetchAuditData]);

  // Excel export - 2 sheets (audit summary + item variance)
  function downloadExcel(orderData, filename = "Audit_Report.xlsx") {
    if (!orderData.length) {
      handleError("No data available for export!");
      return;
    }

    // Sheet 1: Audit level
    const auditSheetData = orderData.map((item, index) => ({
      "Sr. No.": index + 1,
      "Audit Number": item.audit_number || "-",
      "Audit Date": item.audit_date || "-",
      Department: departmentMap[String(item.department_id)] || "-",
      "Total Items": item.total_items,
      "Items with Variance": item.items_with_variance,
      "Excess (₹)": formatNumber(item.total_gain_qty),
      "Shortage (₹)": formatNumber(item.total_loss_qty),
      "Net Variance (₹)": formatNumber(item.net_variance),
      "Audited By": item.audited_by || "-",
      "Audit Notes": item.audit_notes || "-",
    }));

    auditSheetData.push({
      "Sr. No.": "",
      "Audit Number": "",
      "Audit Date": "",
      Department: "",
      "Total Items": "",
      "Items with Variance": stats.totalVariances,
      "Excess (₹)": formatNumber(stats.totalGain),
      "Shortage (₹)": formatNumber(stats.totalLoss),
      "Net Variance (₹)": formatNumber(stats.netVariance),
      "Audited By": "TOTAL",
      "Audit Notes": "",
    });

    // Sheet 2: Item level variance
    const itemSheetData = [];
    orderData.forEach((audit) => {
      (audit.items || []).forEach((item) => {
        itemSheetData.push({
          "Audit Number": audit.audit_number || "-",
          Department: departmentMap[String(audit.department_id)] || "-",
          Ingredient: item.ingredient_name || "-",
          Unit: item.unit_name || "-",
          "System Qty": formatNumber(item.system_qty),
          "Physical Qty": formatNumber(item.physical_qty),
          "Variance Qty": formatNumber(item.variance_qty),
          Type: item.variance_type,
          "Difference Type": item.difference_type || "-",
          Reason: item.reason || "-",
        });
      });
    });

    const auditSheet = XLSX.utils.json_to_sheet(auditSheetData);
    auditSheet["!cols"] = [
      { wch: 8 },
      { wch: 15 },
      { wch: 12 },
      { wch: 18 },
      { wch: 11 },
      { wch: 18 },
      { wch: 12 },
      { wch: 12 },
      { wch: 14 },
      { wch: 18 },
      { wch: 25 },
    ];

    const itemSheet = XLSX.utils.json_to_sheet(itemSheetData);
    itemSheet["!cols"] = [
      { wch: 15 },
      { wch: 18 },
      { wch: 22 },
      { wch: 8 },
      { wch: 11 },
      { wch: 12 },
      { wch: 12 },
      { wch: 8 },
      { wch: 16 },
      { wch: 25 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, auditSheet, "Audit Summary");
    XLSX.utils.book_append_sheet(workbook, itemSheet, "Item Variance");
    XLSX.writeFile(workbook, filename);
  }

  // Pagination
  const totalPages = Math.ceil(auditData.length / entriesPerPage);
  const currentData = auditData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage,
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
    setExpandedAuditId(null);
  };

  const toggleExpand = (auditId) => {
    setExpandedAuditId(expandedAuditId === auditId ? null : auditId);
  };

  // Format date for table display (API sends 'YYYY-MM-DD')
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Initial load
  useEffect(() => {
    fetchFilterData();
    fetchAuditData(startCal, endCal, "1");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainLayout>
      <Header
        title="Audit Report"
        subtitle="Physical stock verification with gain/loss variance analysis"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <ClipboardCheck className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalAudits}
                </p>
                <p className="text-sm text-muted-foreground">Total Audits</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalVariances}
                </p>
                <p className="text-sm text-muted-foreground">Total Variances</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-success">
                  ₹{formatNumber(stats.totalGain)}
                </p>
                <p className="text-sm text-muted-foreground">Excess Value</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold text-destructive">
                  ₹{formatNumber(stats.totalLoss)}
                </p>
                <p className="text-sm text-muted-foreground">Shortage Value</p>
              </div>
            </div>
          </div>
        </div>

        {/* Net Variance Chip */}
        <div className="flex flex-wrap gap-2">
          <span
            className={cn(
              "inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium",
              stats.netVariance > 0
                ? "bg-success/10 text-success"
                : stats.netVariance < 0
                  ? "bg-destructive/10 text-destructive"
                  : "bg-muted text-muted-foreground",
            )}
          >
            Net Variance: {stats.netVariance > 0 ? "+" : ""}₹
            {formatNumber(stats.netVariance)}
          </span>
        </div>

        {/* Filters Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Date Filter Dropdown */}
              <div className="space-y-2">
                <Label>Filter</Label>
                <Select value={filterOption} onValueChange={setFilterOption}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !startCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startCal ? (
                          format(startCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startCal}
                        onSelect={setStartCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endCal ? (
                          format(endCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endCal}
                        onSelect={setEndCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* Department */}
              <div className="space-y-2">
                <Label>Department</Label>
                <Select value={departmentId} onValueChange={setDepartmentId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem
                        key={dept.primary_id}
                        value={String(dept.primary_id)}
                      >
                        {dept.department_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Variance View */}
              <div className="space-y-2">
                <Label>View</Label>
                <Select value={varianceView} onValueChange={setVarianceView}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Audits" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Audits</SelectItem>
                    <SelectItem value="variance">With Variance Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Action Buttons */}
              <div className="flex items-end gap-2">
                <Button
                  onClick={handleSearch}
                  className="gap-2"
                  disabled={showLoaderAdmin}
                >
                  <Search className="w-4 h-4" />
                  Search
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadExcel(auditData)}
                  className="gap-2"
                  disabled={auditData.length === 0}
                >
                  <FileDown className="w-4 h-4" />
                  Export Excel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table Card */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground w-10"></TableHead>
                  <TableHead className="text-muted-foreground">Sr.No</TableHead>
                  <TableHead className="text-muted-foreground">
                    Audit No.
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Audit Date
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Department
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Items
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    With Variance
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Excess (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Shortage (₹)
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Net Variance
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Audited By
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {showLoaderAdmin ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      className="text-center text-muted-foreground"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : currentData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      className="text-center text-muted-foreground"
                    >
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  currentData.map((row, index) => (
                    <Fragment key={row.audit_id || index}>
                      <TableRow
                        className="border-border cursor-pointer"
                        onClick={() => toggleExpand(row.audit_id)}
                      >
                        <TableCell>
                          {expandedAuditId === row.audit_id ? (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell>
                          {(currentPage - 1) * entriesPerPage + index + 1}
                        </TableCell>
                        <TableCell className="font-medium">
                          {row.audit_number || "-"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {formatDate(row.audit_date)}
                        </TableCell>
                        <TableCell>
                          {departmentMap[String(row.department_id)] || "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.total_items}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "text-right font-medium",
                            row.items_with_variance > 0
                              ? "text-warning"
                              : "text-muted-foreground",
                          )}
                        >
                          {row.items_with_variance}
                        </TableCell>
                        <TableCell className="text-right text-success font-medium">
                          {formatNumber(row.total_gain_qty)}
                        </TableCell>
                        <TableCell className="text-right text-destructive font-medium">
                          {formatNumber(row.total_loss_qty)}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "text-right font-medium",
                            row.net_variance > 0
                              ? "text-success"
                              : row.net_variance < 0
                                ? "text-destructive"
                                : "text-muted-foreground",
                          )}
                        >
                          {row.net_variance > 0 ? "+" : ""}
                          {formatNumber(row.net_variance)}
                        </TableCell>
                        <TableCell>{row.audited_by || "-"}</TableCell>
                      </TableRow>

                      {/* Expanded item detail */}
                      {expandedAuditId === row.audit_id && (
                        <TableRow className="border-border bg-muted/30 hover:bg-muted/30">
                          <TableCell colSpan={10} className="p-4">
                            {row.audit_notes && (
                              <p className="text-sm text-muted-foreground mb-3">
                                <span className="font-medium text-foreground">
                                  Notes:
                                </span>{" "}
                                {row.audit_notes}
                              </p>
                            )}
                            <div className="rounded-lg border border-border bg-card overflow-hidden">
                              <Table>
                                <TableHeader>
                                  <TableRow className="border-border hover:bg-transparent">
                                    <TableHead className="text-muted-foreground text-xs">
                                      Ingredient
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs">
                                      Unit
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs text-right">
                                      System Qty
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs text-right">
                                      Physical Qty
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs text-right">
                                      Variance Qty
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs">
                                      Type
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs">
                                      Difference Type
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs">
                                      Reason
                                    </TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {(row.items || []).length === 0 ? (
                                    <TableRow>
                                      <TableCell
                                        colSpan={8}
                                        className="text-center text-sm text-muted-foreground"
                                      >
                                        No items found for this audit
                                      </TableCell>
                                    </TableRow>
                                  ) : (
                                    row.items.map((item, i) => (
                                      <TableRow
                                        key={
                                          item.detail_id ||
                                          `${row.audit_id}-${item.ingredient_id}-${i}`
                                        }
                                        className={cn(
                                          "border-border",
                                          item.variance_type === "SHORT" &&
                                            "bg-destructive/5",
                                          item.variance_type === "EXCESS" &&
                                            "bg-success/5",
                                        )}
                                      >
                                        <TableCell className="font-medium text-sm">
                                          {item.ingredient_name || "-"}
                                        </TableCell>
                                        <TableCell className="text-sm">
                                          {item.unit_name || "-"}
                                        </TableCell>
                                        <TableCell className="text-right text-sm">
                                          {formatNumber(item.system_qty)}
                                        </TableCell>
                                        <TableCell className="text-right text-sm font-medium">
                                          {formatNumber(item.physical_qty)}
                                        </TableCell>
                                        <TableCell
                                          className={cn(
                                            "text-right text-sm font-medium",
                                            item.variance_qty > 0
                                              ? "text-success"
                                              : item.variance_qty < 0
                                                ? "text-destructive"
                                                : "text-muted-foreground",
                                          )}
                                        >
                                          {item.variance_qty > 0 ? "+" : ""}
                                          {formatNumber(item.variance_qty)}
                                        </TableCell>
                                        <TableCell>
                                          <span
                                            className={cn(
                                              "inline-flex items-center px-2 py-1 rounded-md text-xs font-medium",
                                              varianceTypeBadgeClass(
                                                item.variance_type,
                                              ),
                                            )}
                                          >
                                            {item.variance_type}
                                          </span>
                                        </TableCell>
                                        <TableCell className="text-sm">
                                          {item.difference_type || "-"}
                                        </TableCell>
                                        <TableCell
                                          className="text-sm max-w-[150px] truncate"
                                          title={item.reason || ""}
                                        >
                                          {item.reason || "-"}
                                        </TableCell>
                                      </TableRow>
                                    ))
                                  )}
                                </TableBody>
                              </Table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
                {Math.min(currentPage * entriesPerPage, auditData.length)} of{" "}
                {auditData.length} entries
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={pageNumber}
                        variant={
                          pageNumber === currentPage ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber}>...</span>;
                  }
                  return null;
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
